import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ThinkingBlock from './ThinkingBlock';
import ToolUseBlock from './ToolUseBlock';
import WorkflowPreviewCard from './WorkflowPreviewCard';
import WorkflowProgressIndicator from './WorkflowProgressIndicator';
import WorkflowConfirmationCard from './WorkflowConfirmationCard';
import { Sparkles, Copy, Check, FileText, Image } from 'lucide-react';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      title="Copy code"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      <span>{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );
}

// Custom theme that works for both light and dark via CSS variables
const softDarkTheme = {
  ...oneLight,
  'pre[class*="language-"]': {
    ...oneLight['pre[class*="language-"]'],
    background: 'transparent',
  },
  'code[class*="language-"]': {
    ...oneLight['code[class*="language-"]'],
    background: 'transparent',
  },
};

function CodeBlock({ className, children }) {
  const text = String(children).replace(/\n$/, '');
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  return (
    <div className="rounded-xl overflow-hidden my-3 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100/80 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{language || 'text'}</span>
        <CopyButton text={text} />
      </div>
      <div className="dark:hidden">
        <SyntaxHighlighter
          style={softDarkTheme}
          language={language || 'text'}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: 0,
            padding: '1rem 1.25rem',
            fontSize: '0.8125rem',
            lineHeight: '1.6',
            background: 'transparent',
          }}
          codeTagProps={{
            style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }
          }}
        >
          {text}
        </SyntaxHighlighter>
      </div>
      <div className="hidden dark:block">
        <SyntaxHighlighter
          style={softDarkTheme}
          language={language || 'text'}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: 0,
            padding: '1rem 1.25rem',
            fontSize: '0.8125rem',
            lineHeight: '1.6',
            background: 'transparent',
            color: '#e2e8f0',
          }}
          codeTagProps={{
            style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }
          }}
        >
          {text}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

// Workflow tool names that modify workflow state
const WORKFLOW_BUILD_TOOLS = new Set([
  'create_workflow', 'add_workflow_node', 'connect_workflow_nodes',
  'configure_workflow_node', 'activate_workflow', 'get_workflow_summary',
  'remove_workflow_node', 'delete_workflow',
]);

/**
 * Accumulate workflow state from tool uses in a single message.
 * Tracks workflow_id, name, nodes, edges, and status across tool calls.
 */
function useWorkflowState(toolUses) {
  return useMemo(() => {
    let workflow_id = null;
    let name = '';
    let status = 'draft';
    const nodes = [];
    const edges = [];
    const completedTools = [];
    let hasWorkflowTools = false;
    let allDone = true;
    let isDeleted = false;

    for (const tu of toolUses) {
      if (!WORKFLOW_BUILD_TOOLS.has(tu.name) && !tu.name?.startsWith('list_')) continue;

      if (WORKFLOW_BUILD_TOOLS.has(tu.name)) {
        hasWorkflowTools = true;
        if (tu.done) completedTools.push(tu.name);
        if (!tu.done) allDone = false;
      }

      if (!tu.output || !tu.done) continue;

      try {
        const data = JSON.parse(tu.output);
        if (!data.success) continue;

        switch (tu.name) {
          case 'create_workflow':
            workflow_id = data.workflow_id;
            name = data.name || '';
            break;
          case 'add_workflow_node':
            nodes.push({
              id: data.node_id,
              type: data.node_type,
              label: data.label,
            });
            break;
          case 'connect_workflow_nodes':
            edges.push({
              id: data.edge_id,
              source: tu.input?.source_node_id,
              target: tu.input?.target_node_id,
            });
            break;
          case 'activate_workflow':
            status = 'active';
            break;
          case 'delete_workflow':
            isDeleted = true;
            break;
          case 'get_workflow_summary':
            if (data.workflow_id) workflow_id = data.workflow_id;
            if (data.name) name = data.name;
            if (data.status) status = data.status;
            // Rebuild nodes from summary
            if (data.nodes && data.nodes.length > 0) {
              nodes.length = 0;
              data.nodes.forEach(n => nodes.push({
                id: n.id,
                type: n.type,
                label: n.label,
              }));
            }
            break;
          default:
            break;
        }
      } catch {
        // ignore parse errors
      }
    }

    if (!hasWorkflowTools || isDeleted) return null;

    return {
      workflow_id,
      name,
      nodes,
      edges,
      status,
      completedTools,
      allDone,
      isComplete: allDone && nodes.length > 0 && edges.length > 0,
    };
  }, [toolUses]);
}

export default function MessageBubble({ message, onSendMessage }) {
  const isUser = message.role === 'user';
  const workflowState = useWorkflowState(!isUser ? (message.toolUses || []) : []);

  if (isUser) {
    const attachments = message.attachments;
    return (
      <div className="flex justify-end mb-6">
        <div className="max-w-[75%]">
          {attachments?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1.5 justify-end">
              {attachments.map((att, i) => {
                const isImage = att.mime_type?.startsWith('image/');
                return (
                  <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border border-brand-200/60 dark:border-brand-800/40 bg-brand-50/50 dark:bg-brand-950/20 text-brand-700 dark:text-brand-300">
                    {isImage ? <Image className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                    <span className="max-w-[120px] truncate">{att.filename}</span>
                    {att.size_bytes && (
                      <span className="opacity-60">
                        {att.size_bytes < 1024 * 1024
                          ? `${(att.size_bytes / 1024).toFixed(1)} KB`
                          : `${(att.size_bytes / (1024 * 1024)).toFixed(1)} MB`}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {message.content && (
            <div className="bg-brand-50 dark:bg-brand-950/30 border border-brand-200/60 dark:border-brand-800/40 rounded-2xl rounded-tr-sm px-4 py-2.5 text-[0.9375rem] leading-[1.75] text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
              {message.content}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Assistant message — open layout, no bubble
  return (
    <div className="flex justify-start mb-6">
      <div className="flex items-start gap-3 w-[85%]">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-green-100 to-green-100 dark:from-green-900/40 dark:to-green-900/40 flex items-center justify-center mt-1">
          <Sparkles className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          {message.thinking && (
            <ThinkingBlock thinking={message.thinking} done={message.thinkingDone} />
          )}

          {message.toolUses.map(tu => (
            <ToolUseBlock key={tu.id} toolUse={tu} />
          ))}

          {/* Workflow UI: progress indicator during building */}
          {workflowState && !workflowState.isComplete && workflowState.completedTools.length > 0 && (
            <WorkflowProgressIndicator
              completedTools={workflowState.completedTools}
              isComplete={false}
            />
          )}

          {/* Workflow UI: preview card when nodes are added */}
          {workflowState && workflowState.nodes.length > 0 && !workflowState.isComplete && (
            <WorkflowPreviewCard workflowState={workflowState} />
          )}

          {/* Workflow UI: confirmation card when workflow is complete */}
          {workflowState && workflowState.isComplete && !message.isStreaming && (
            <WorkflowConfirmationCard
              workflowState={workflowState}
              onSendMessage={onSendMessage}
            />
          )}

          {message.content && (
            <div className="text-[0.9375rem] leading-[1.75] text-gray-800 dark:text-gray-200 [&>*:first-child]:mt-0">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p({ children }) {
                    return <p className="my-3">{children}</p>;
                  },
                  h1({ children }) {
                    return <h1 className="text-xl font-semibold mt-5 mb-2 text-gray-900 dark:text-gray-100">{children}</h1>;
                  },
                  h2({ children }) {
                    return <h2 className="text-lg font-semibold mt-5 mb-2 text-gray-900 dark:text-gray-100">{children}</h2>;
                  },
                  h3({ children }) {
                    return <h3 className="text-base font-semibold mt-4 mb-1.5 text-gray-900 dark:text-gray-100">{children}</h3>;
                  },
                  ul({ children }) {
                    return <ul className="my-3 ml-1 space-y-1.5 list-disc list-outside pl-5 marker:text-gray-400 dark:marker:text-gray-500">{children}</ul>;
                  },
                  ol({ children }) {
                    return <ol className="my-3 ml-1 space-y-1.5 list-decimal list-outside pl-5 marker:text-gray-400 dark:marker:text-gray-500">{children}</ol>;
                  },
                  li({ children }) {
                    return <li className="pl-0.5">{children}</li>;
                  },
                  strong({ children }) {
                    return <strong className="font-semibold text-gray-900 dark:text-gray-100">{children}</strong>;
                  },
                  blockquote({ children }) {
                    return (
                      <blockquote className="my-3 border-l-2 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400">
                        {children}
                      </blockquote>
                    );
                  },
                  hr() {
                    return <hr className="my-4 border-gray-200 dark:border-gray-700" />;
                  },
                  table({ children }) {
                    return (
                      <div className="my-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">{children}</table>
                      </div>
                    );
                  },
                  th({ children }) {
                    return <th className="px-3 py-2 text-left font-semibold bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">{children}</th>;
                  },
                  td({ children }) {
                    return <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">{children}</td>;
                  },
                  pre({ children }) {
                    return <>{children}</>;
                  },
                  code({ className, children, node, ...props }) {
                    const hasLanguage = /language-(\w+)/.test(className || '');
                    const hasNewlines = String(children).includes('\n');

                    if (hasLanguage || hasNewlines) {
                      return <CodeBlock className={className}>{children}</CodeBlock>;
                    }

                    return (
                      <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-md text-[0.8125rem] font-mono text-gray-800 dark:text-gray-200 border border-gray-200/60 dark:border-gray-700/60" {...props}>
                        {children}
                      </code>
                    );
                  },
                  a({ children, ...props }) {
                    return (
                      <a {...props} className="text-brand-600 dark:text-brand-400 underline underline-offset-2 decoration-brand-300 dark:decoration-brand-700 hover:decoration-brand-500 transition-colors" target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {message.isStreaming && !message.content && !message.thinking && message.toolUses.length === 0 && (
            <div className="flex items-center gap-1.5 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
